<template>
  <div>
    <v-dialog
      v-if="orderItem"
      v-model="showDialog"
      scrollable
      hide-overlay
      :max-width="
        $vuetify.breakpoint.lgAndUp
          ? '70%'
          : $vuetify.breakpoint.mdAndUp
          ? '90%'
          : '100%'
      "
    >
      <v-card class="rounded-lg" color="card" height="100%">
        <v-container fluid class="pa-6">
          <!--heading and close-->
          <v-row align="center">
            <v-col cols="auto">
              <span
                class="font-weight-medium text-h5 text-start textLight--text"
              >
                {{ $t("invoice.courseDetails") }}
              </span>
            </v-col>

            <v-spacer></v-spacer>

            <v-col cols="auto">
              <v-icon @click="showDialog = false"> mdi-close </v-icon>
            </v-col>
          </v-row>

          <!--body-->
          <v-row class="text-subtitle-1 font-weight-regular textLight--text">
            <v-col>
              <ValidationObserver ref="orderItemObserver">
                <!--training title-->
                <v-row
                  justify="space-between"
                  align="center"
                  class="text-start"
                >
                  <v-col v-if="$vuetify.breakpoint.smAndUp" cols="12" sm="3">
                    <span>
                      {{ $t("invoice.name") }}
                    </span>
                  </v-col>

                  <v-col cols="12" sm="8">
                    <span>
                      {{ getLocalisedValue(orderItem.trainingTitle) }}
                    </span>
                  </v-col>
                </v-row>

                <!--content-->
                <v-row justify="space-between" class="text-start">
                  <v-col cols="12" sm="6">
                    <!--total employees-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" sm="7">
                        <span>
                          {{ $t("invoice.totalEmployees") }}
                        </span>
                      </v-col>

                      <v-col cols="6" sm="4">
                        <span>
                          {{ orderItem.filteredTrainingRegistrations.length }}
                        </span>
                      </v-col>
                    </v-row>

                    <!--total completed-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" sm="7">
                        <v-checkbox
                          v-model="orderItem.isCompletedCountSelected"
                          :label="$t('invoice.completed')"
                          hide-details
                          :disabled="orderItem.completedCount < 1"
                          class="ma-0 font-weight-light"
                        >
                        </v-checkbox>
                      </v-col>

                      <v-col cols="6" sm="4">
                        <span class="font-weight-light">
                          {{ orderItem.completedCount }}
                        </span>
                      </v-col>
                    </v-row>

                    <!--total started-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" sm="7">
                        <v-checkbox
                          v-model="orderItem.isStartedCountSelected"
                          :label="$t('invoice.started')"
                          hide-details
                          :disabled="orderItem.startedCount < 1"
                          class="ma-0 font-weight-light"
                        >
                        </v-checkbox>
                      </v-col>

                      <v-col cols="6" sm="4">
                        <span class="font-weight-light">
                          {{ orderItem.startedCount }}
                        </span>
                      </v-col>
                    </v-row>

                    <!--total registered-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" sm="7">
                        <v-checkbox
                          v-model="orderItem.isRegisteredCountSelected"
                          :label="$t('invoice.registered')"
                          hide-details
                          :disabled="orderItem.registeredCount < 1"
                          class="ma-0 font-weight-light"
                        >
                        </v-checkbox>
                      </v-col>

                      <v-col cols="6" sm="4">
                        <span class="font-weight-light">
                          {{ orderItem.registeredCount }}
                        </span>
                      </v-col>
                    </v-row>

                    <!--divider-->
                    <v-row align="center">
                      <v-col class="align-self-end">
                        <v-divider></v-divider>
                      </v-col>
                    </v-row>

                    <!--total quantity-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" sm="7">
                        <span>
                          {{ $t("invoice.totalQuantity") }}
                        </span>
                      </v-col>

                      <v-col cols="6" sm="4">
                        <span>
                          {{ calculateTotalQuantity(orderItem) }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="12" sm="6" md="5" class="align-self-end">
                    <!--unit price-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" md="4">
                        <span>
                          {{ $t("invoice.unitPrice") }}
                        </span>
                      </v-col>

                      <v-col cols="6" md="7">
                        <validation-provider v-slot="{ errors }">
                          <v-text-field
                            v-model.number="orderItem.unitPrice"
                            :prefix="`${currency} `"
                            :error-messages="errors"
                            dense
                            hide-details
                            type="number"
                            :min="0"
                            @keypress="
                              numberInputEventFilter({
                                event: $event,
                                isFloat: true,
                              })
                            "
                            @paste="
                              numberInputEventFilter({
                                event: $event,
                                isFloat: true,
                              })
                            "
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--discount-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" md="4">
                        <span>
                          {{ $t("invoice.discount") }}
                        </span>
                      </v-col>

                      <v-col cols="6" md="7">
                        <validation-provider v-slot="{ errors }">
                          <v-text-field
                            v-model.number="orderItem.discount"
                            :prefix="`${currency} `"
                            :error-messages="errors"
                            dense
                            hide-details
                            type="number"
                            :min="0"
                            @keypress="
                              numberInputEventFilter({
                                event: $event,
                                isFloat: true,
                              })
                            "
                            @paste="
                              numberInputEventFilter({
                                event: $event,
                                isFloat: true,
                              })
                            "
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--total amount-->
                    <v-row justify="space-between" align="center">
                      <v-col cols="6" md="4">
                        <span>
                          {{ $t("invoice.totalAmount") }}
                        </span>
                      </v-col>

                      <v-col cols="6" md="7">
                        <span class="font-weight-medium">
                          {{
                            `${currency} ${formatPrice(
                              calculateTotalAmount(orderItem)
                            )}`
                          }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </ValidationObserver>
            </v-col>
          </v-row>

          <!--action-->
          <v-row>
            <v-col>
              <v-btn
                depressed
                block
                color="primary"
                small
                class="rounded-lg font-weight-normal text-capitalize"
                :disabled="calculateTotalAmount(orderItem) <= 0"
                @click="submitOrderItem(orderItem)"
              >
                <v-icon>mdi-plus</v-icon>

                {{
                  orderItem.index != null
                    ? $t("invoice.updateCourse")
                    : $t("invoice.addCourse")
                }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import "../../core-module/util/validationRules";

export default {
  name: "InvoiceOrderItemDialog",
  props: {
    showDialogProp: {
      type: Boolean,
      required: true,
    },
    orderItemProp: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDialog: this.showDialogProp,
      orderItem: this.orderItemProp,
    };
  },
  computed: {
    currency() {
      return this.$store.getters.currency;
    },
  },
  watch: {
    showDialogProp() {
      this.showDialog = this.showDialogProp;
    },
    orderItemProp() {
      this.orderItem = this.orderItemProp;
    },
    showDialog() {
      this.$emit("update:showDialogProp", this.showDialog);
    },
  },
  methods: {
    calculateTotalQuantity(orderItem) {
      let quantity = 0;

      if (orderItem.isCompletedCountSelected) {
        quantity += orderItem.completedCount;
      }

      if (orderItem.isStartedCountSelected) {
        quantity += orderItem.startedCount;
      }

      if (orderItem.isRegisteredCountSelected) {
        quantity += orderItem.registeredCount;
      }

      return quantity;
    },
    calculateTotalAmount(orderItem) {
      const quantity = this.calculateTotalQuantity(orderItem);

      return (
        quantity *
        ((orderItem.unitPrice ?? orderItem.trainingPrice ?? 0) -
          (orderItem.discount ?? 0))
      );
    },
    submitOrderItem(orderItem) {
      const trainingRegistrationDetailsList =
        orderItem.filteredTrainingRegistrations.reduce((result, x) => {
          const canPush =
            (orderItem.isRegisteredCountSelected &&
              x.status === "REGISTERED") ||
            (orderItem.isStartedCountSelected && x.status === "THIRDPARTY") ||
            (orderItem.isCompletedCountSelected && x.status === "COMPLETED");

          if (canPush) {
            result.push({
              trainingRegistration: x._id,
              employee: x.employee._id,
            });
          }

          return result;
        }, []);

      this.$emit("onSubmit", {
        ...orderItem,
        trainingRegistrationDetailsList,
        unitPrice: orderItem.unitPrice ?? orderItem.trainingPrice,
        discount: orderItem.discount ?? 0,
        quantity: this.calculateTotalQuantity(orderItem),
        amount: this.calculateTotalAmount(orderItem),
      });

      this.showDialog = false;
      this.orderItem = null;
    },
  },
};
</script>
